import {RefObject} from 'react';
import {useAdjustXAxisScrollPosition} from '../../../common/shared/hooks/useAdjustXAsixScrollPosition';
import {twMerge} from '../../../common/twMerge';
import {Separator} from '../../atoms/Separator/Separator';
import {BelowDefaultTabMainProps} from './BelowDefaultTabMainProps.interface';
import {BelowTabButton, BelowTabButtonType} from './BelowTabButton';
import {BelowTabCheckBox, BelowTabCheckBoxType} from './BelowTabCheckBox';
import {BelowTabSelectBox, BelowTabSelectBoxType} from './BelowTabSelectBox';
import {
  BelowTabSelectLabel,
  BelowTabSelectLabelType,
} from './BelowTabSelectLabel';
import {BelowTabTextButton, BelowTabTextButtonType} from './BelowTabTextButton';
import {BelowTabTextLabel, BelowTabTextLabelType} from './BelowTabTextLabel';
import {getByType} from './functions';

function BelowDefaultTabMain({
  initialSelectLabelIndex,
  children,
  className,
  ...props
}: BelowDefaultTabMainProps) {
  const {tabRef, onSelectTabItem} = useAdjustXAxisScrollPosition(
    initialSelectLabelIndex
  );

  const belowTabTextLabelList = getByType({
    children,
    type: BelowTabTextLabelType,
  });
  const belowTabSelectLabelList = getByType({
    children,
    type: BelowTabSelectLabelType,
  });
  const belowTabTextButtonList = getByType({
    children,
    type: BelowTabTextButtonType,
  });
  const belowTabButtonList = getByType({
    children,
    type: BelowTabButtonType,
  });
  const belowTabCheckBoxList = getByType({
    children,
    type: BelowTabCheckBoxType,
  });
  const belowTabSelectBoxList = getByType({
    children,
    type: BelowTabSelectBoxType,
  });

  const hasSelectLabel = belowTabSelectLabelList.length > 0;

  return (
    <div
      className={twMerge(
        'flex h-44pxr w-full flex-row items-center justify-between bg-theme-bg-base',
        className
      )}
      {...props}
    >
      {hasSelectLabel && (
        <div
          ref={tabRef as RefObject<HTMLDivElement>}
          className="flex h-full flex-1 items-center overflow-x-auto whitespace-nowrap pr-24pxr pl-15pxr scrollbar-hide"
        >
          {belowTabSelectLabelList.map((selectLabel, idx) => (
            <div
              key={`below_select_label_${idx}`}
              className="relative mx-15pxr flex h-full items-center first-of-type:ml-0 last-of-type:mr-0"
            >
              <div onClick={e => onSelectTabItem(e.target as HTMLElement)}>
                {selectLabel}
              </div>
              {idx < belowTabSelectLabelList.length - 1 && (
                <Separator className="absolute top-17pxr -right-15pxr h-10pxr bg-theme-transparent-20" />
              )}
            </div>
          ))}
        </div>
      )}
      {!hasSelectLabel && (
        <div className="flex h-full flex-1 items-center space-x-8pxr">
          {belowTabTextLabelList}
          {belowTabTextButtonList}
          {belowTabButtonList}
        </div>
      )}
      <div
        className={twMerge(
          'relative flex h-full items-center space-x-16pxr',
          !hasSelectLabel && 'ml-16pxr'
        )}
      >
        {hasSelectLabel && (
          <>
            <div
              className="pointer-events-none absolute top-0 -left-24pxr h-full w-24pxr"
              style={{
                background:
                  'linear-gradient(to right, rgb(var(--colors-theme-bg-base) / 0%), rgb(var(--colors-theme-bg-base)))',
              }}
            />
          </>
        )}
        {belowTabCheckBoxList}
        {belowTabSelectBoxList}
      </div>
    </div>
  );
}

export const BelowDefaultTab = Object.assign(BelowDefaultTabMain, {
  TextLabel: BelowTabTextLabel,
  SelectLabel: BelowTabSelectLabel,
  TextButton: BelowTabTextButton,
  Button: BelowTabButton,
  CheckBox: BelowTabCheckBox,
  SelectBox: BelowTabSelectBox,
});
